import { RWSService } from "@rws-framework/client";
import StorageService, { type StorageServiceInstance } from "src/services/StorageService";
import InsightsDashboardService, { type InsightsDashboardServiceInstance } from "./InsightsDashboardService";
import Cookies from "js-cookie";
import { human_time_ago, my_format } from "src/helpers/dateUtils";
import type { Activity, ActivityStudentScore } from "../types/activities";
import { ActivityService } from "./ActivityService";

export type EmojiOption = { label: string };

export type GradebookItem = {
  activity: string;
  activity_label: string;
  activity_url: string;
  activity_results_url: string;
  grade: number | string;
  grade_label: string;
  grade_total: number | string;
  grade_messages: EmojiOption[];
  due_date: string;
  orig_due_date?: string;
  due_time?: string;
  due_date_label: string;
  date_past?: boolean;
  submit_date: string;
  submit_time?: string;
  submit_date_label: string;
  open_date_in_future?: string;
  open_date?: string;
  type: string;
};

export type GradebookGroup = {
  title: string;
  score_to_date: number | string;
  score_to_date_attempted: number | string;
  score_to_date_obtained: number | string;
  footer_message: string;
  color?: string;
  open_date_in_future: boolean;
  open_date?: string;
  items: GradebookItem[];
};

export type GradebookState = {
  score_to_date: number | string;
  course_total: number;
  no_total_column: boolean;
  block_total_scores: boolean;
  score_to_date_attempted: number | string;
  score_to_date_obtained: number | string;
  groups: GradebookGroup[];
  emoji_options: EmojiOption[];
  cached: string;
  avg: { activity_student_score: ActivityStudentScore; activity_manager: Activity[] };
  rollup: boolean;
  using_rollup: boolean;
};

export type GradebookFetchOptions = { no_custom_rollup?: boolean };

const emoji_options: EmojiOption[] = [
  {
    "label": "Not Available",
  },
  {
    "label": "Available Later",
  },
  {
    "label": "Still More Attempts",
  },
  {
    "label": "Overdue",
  },
  {
    "label": "Pending",
  },
  {
    "label": "Done",
  },
  {
    "label": "Due",
  },
  {
    "label": "Upcoming",
  },
  {
    "label": "Late work auto-deduction",
  },
  {
    "label": "Cached",
  }
  ];

export class InsightsGradebookService extends RWSService {
  static _IN_CLIENT: boolean = true;

  constructor(
    @InsightsDashboardService private dashboardService: InsightsDashboardServiceInstance,
    @StorageService private storageService: StorageServiceInstance,
  ) {
    super();
  }

  flush() {
    this.dashboardService.flush();
  }

  /** Alias kept for the AngularJS callers that still use the old `insightsGradebookService.fetch()` API. */
  fetch(options: GradebookFetchOptions = null): Promise<GradebookState> {
    return this.fetch_data(options);
  }

  fetch_data(options: GradebookFetchOptions = null): Promise<GradebookState> {
    if (!["custom", "lesson"].includes(Cookies.get('viewL'))) {
      Cookies.set('viewL', "custom");
    }

    return new Promise((resolve, reject) => {
      const now = new Date();
      const state = {} as GradebookState;
      this.dashboardService.get('gradebook', options).then((gradebook) => {
        const user_id = this.storageService.get('prop', 'mongoId');
        const scores = gradebook.user_scores[user_id];
        const activity_student_score = gradebook.activity_student_score;
        const activities = gradebook.activity_manager.filter(x => !x.suppress_until_available || (x.suppress_until_available && x.open_date_in_future));
        state.score_to_date = (scores.percent||scores.percent==0) ? scores.percent : '-';
        state.course_total = gradebook.course_max_score;
        state.no_total_column = gradebook.no_total_column;
        state.block_total_scores = gradebook.block_total_scores
        state.score_to_date_attempted = (scores.attempted||scores.attempted==0) ? scores.attempted : '-';
        state.score_to_date_obtained = (scores.score||scores.score==0) ? scores.score : '-';
        state.groups = [];
        state.emoji_options = emoji_options;
        state.cached = human_time_ago(gradebook.cached);
        state.avg = {activity_student_score: activity_student_score, activity_manager: activities}
        state.rollup = gradebook.has_rollup;
        state.using_rollup = gradebook.using_custom_rollup;

        let items: GradebookItem[] = [];
        activities.forEach((activity) => {

          // here we decide NOT to show the activites not tied to any lesson if activity.show is NOT set
          // and remove the course_data_only items of course
          if (
            activity.course_data_only ||
            !activity.show &&
            (activity.type == "custom" || (!activity.lesson && activity.type !== "lesson"))
          ) return;

          // lesson rollup or course column
          if (activity.type == 'lesson' || activity.link == '#/' + activity.id) {
            // in the case of lesson rollup, we use the _ prepend for lesson rollup columns
            const rollup_id = (state.using_rollup? "": "_") + activity.id;
            const sc = (activity_student_score[rollup_id] ||{})[user_id] || {};
            // insert collected items into the current group which is now being replaced by the new group
            if (state.groups.length) {
              state.groups[state.groups.length-1].items = [...items];
              items = [];
            }
            // new group started
            const temp = activities.find(act => act.id === activity.id && act.open_date);
            state.groups.push({
              title: activity.title,
              score_to_date: (sc.percent || sc.percent == 0)? sc.percent : '-',
              score_to_date_attempted: (sc.attempted || sc.attempted == 0)? sc.attempted : '-',
              score_to_date_obtained: (sc.score || sc.score == 0)? sc.score : '-',
              footer_message: '',
              color: activity.color,
              open_date_in_future: new Date(temp?.open_date) > now,
              open_date: temp?.open_date,
              items: []
            });
          }
          // not lesson rollup
          if (activity.type !== "lesson") {
            const score = (activity_student_score[activity.activity_id || activity.id]||{})[user_id] || {};
            let due_date = activity['student_due'] || activity['display_due_date']; // student_due can be 2199 to indicate due is removed
            let submit_time, due_time, due_past;
            let submit_date = score['dt_completed'] || score['dt_started'] || score['not_displaying_score'];

            if (['project', 'comments'].includes(activity.type)) {
              submit_date = score['submitted_at'];
            }

            // an LTI 1.3 grade without a submission keeps the grade's date
            if (ActivityService.isLti13(activity)) {
              submit_date = score['submitted_at'] ?? submit_date;
            }

            if (submit_date && submit_date.indexOf('Z')<0) {
              submit_date += 'Z'
            }

            let orig_due_date;
            if (
                 activity['display_due_date']
              && activity['display_due_date'] < '2198'
              && activity['display_due_date'] != "-/-"
              && due_date !== activity['display_due_date']
            ) {
              orig_due_date = my_format(activity['display_due_date']).slice(0,2).join(" ");
            }

            if (due_date == '-/-' || due_date > '2198') { // here the student_due removes the date
              due_date = '-';
            } else {
              [due_date, due_time, due_past] = my_format(due_date);
            }

            if (submit_date) {
              [submit_date, submit_time] = my_format(submit_date);
            } else {
              submit_date = '-'
            }
            const item: GradebookItem = {
              activity: activity.report_title,
              activity_label: 'Activity',
              activity_url: activity.link,
              activity_results_url: activity.link + "/" + activity.id,
              grade: this.helper(score.score),
              grade_label: 'Score',
              grade_total: this.helper(activity.max_score),
              grade_messages: this._grade_messages(score),
              due_date: due_date,
              orig_due_date: orig_due_date,
              due_time: due_time,
              due_date_label: "Due",
              date_past: due_past,
              submit_date: submit_date,
              submit_time: submit_time,
              submit_date_label: 'Submitted',
              open_date_in_future: activity.open_date_in_future,
              open_date: activity.open_date,
              type: activity.type
            };

            items.push(item);
          }
        });

        // last group needs items too
        if (items.length && state.groups.length) {
          state.groups[state.groups.length-1].items = items;
        }

        resolve(state)
        return state
      }).catch(error => { reject(error) })
    })
  
  }

  private helper(score) {
    if (!score && score != 0) return '-';
    if (isNaN(score) || typeof(score) == typeof("")) return score;

    return parseFloat((score).toFixed(1));
  }

  private _grade_messages(score) {
    const grade_messages = [];
    if (score.not_displaying_score) {
      grade_messages.push(emoji_options[0]); // Not Available
      // contains_overdue and instructor_graded_this are wrong when not displaying score
      // since we filter out scores early. thus we only show this one icon
      return grade_messages;
    }
    if (score.not_displaying_score_yet) {
      grade_messages.push(emoji_options[1]); // Available Later
      // ditto
      return grade_messages;
    }
    if (score.after_max_attempts) grade_messages.push(emoji_options[2]); // Still More Attempts
    if (score.contains_overdue && score.project_reopened) grade_messages.push(emoji_options[6]); // Due
    if (score.contains_overdue && !score.overdue_accepted) grade_messages.push(emoji_options[3]); // Overdue
    if (score.overdue_accepted) grade_messages.push(emoji_options[8]); // Late work auto-deduction
    if (score.needs_grading) grade_messages.push(emoji_options[4]); // Pending
    if (score.instructor_graded_this) grade_messages.push(emoji_options[5]); // Done
    return grade_messages;
  }
}

export default InsightsGradebookService.getSingleton()

export {InsightsGradebookService as InsightsGradebookServiceInstance}
